import { HOURS_PER_DAY, TODAY } from '../../constants';
import type { Dev, HoursByBrick, HoursByBrickAndDev, Phase } from '../../types/domain';
import { contractMonthsElapsed, isoStr } from '../dates';
import { toNumber } from '../numbers';
import { phaseBrickId } from './identifiers';
import { phaseSoldDays } from './sold';
import type { HourlyCosts, PhaseMarginData } from './types';

// Map devId → coût horaire (€/h)
export function devCostMap(devs: Dev[] | null | undefined): HourlyCosts {
  const m: HourlyCosts = {};
  for (const d of devs || []) m[d.id] = toNumber(d.coutHoraire || 0);
  return m;
}

// Heures réellement passées sur une phase (feuilles d'heures de toutes les briques de la phase,
// + niveau projet si la phase est active).
// Le tiroir "général par phase" (phaseBriqueId) est toujours celui de CETTE phase — plus besoin de
// deviner via isActive (avant, tout le temps général du client basculait en bloc sur la phase jugée
// "active aujourd'hui", quelle que soit la date réelle des heures).
export function phaseHoursSpent(phase: Phase, tsCons: HoursByBrick): number {
  const ids = [...(phase.briques || []).map((b) => b.id), phaseBrickId(phase.id)];
  return ids.reduce((s, bid) => s + (tsCons[bid] || 0), 0);
}

// Heures passées par chaque dev sur une phase → { [devId]: heures }.
export function phaseHoursByDev(phase: Phase, tsByDev: HoursByBrickAndDev): Record<string, number> {
  const ids = [...(phase.briques || []).map((b) => b.id), phaseBrickId(phase.id)];
  const map: Record<string, number> = {};
  for (const bid of ids) {
    const devHours = tsByDev[bid] || {};
    for (const [devId, h] of Object.entries(devHours)) map[devId] = (map[devId] || 0) + h;
  }
  return map;
}

// Coût réel d'une phase = Σ (heures passées par chaque dev) × (coût horaire du dev).
export function phaseCost(phase: Phase, tsByDev: HoursByBrickAndDev, costMap: HourlyCosts): number {
  const ids = [...(phase.briques || []).map((b) => b.id), phaseBrickId(phase.id)];
  let cost = 0;
  for (const bid of ids) {
    const devHours = tsByDev[bid] || {};
    for (const [devId, h] of Object.entries(devHours)) cost += h * (costMap[devId] || 0);
  }
  return cost;
}

// Chiffre d'affaires vendu (€) selon le type de facturation.
export function phaseRevenue(phase: Phase, hoursSpent: number, todayIso = isoStr(TODAY)): number {
  const tjm = toNumber(phase.tjm || 0);
  const fact = phase.facturation || 'forfait_brique';
  const amount = toNumber(phase.montantGlobal || 0);
  if (fact === 'forfait_global') return amount;
  if (fact === 'regie_temporelle' || fact === 'regie_horaire')
    return (hoursSpent / HOURS_PER_DAY) * tjm; // facturé au réel
  // Maintenance : marge « à date », mensuel × mois déjà commencés (reconduction tacite comprise) ;
  // sans date de début, la durée saisie.
  if (fact === 'maintenance_corrective' || fact === 'maintenance_evolutive') {
    const mois = phase.dateDebut
      ? contractMonthsElapsed(phase.dateDebut, phase.dateFin, todayIso)
      : toNumber(phase.dureeMois || 0);
    return amount * mois;
  }
  // Forfait par brique : le budget vendu de l'affaire (= jours × TJM, piloté par le CRM) ; pour une
  // phase hors CRM, jours de référence × TJM.
  return amount > 0 ? amount : phaseSoldDays(phase) * tjm;
}

// Chiffres de marge arrondis : { vendu, cout, marge, marginPercent, heures }.
export function marginData(vendu: number, cout: number, heures: number): PhaseMarginData {
  const marge = vendu - cout;
  return {
    vendu: Math.round(vendu),
    cout: Math.round(cout),
    marge: Math.round(marge),
    marginPercent: vendu > 0 ? Math.round((marge / vendu) * 100) : null,
    heures,
  };
}

// Récapitulatif marge d'une phase (à date pour une maintenance), ou null si non calculable.
export function phaseMarginOf(
  phase: Phase,
  tsCons: HoursByBrick,
  tsByDev: HoursByBrickAndDev,
  costMap: HourlyCosts,
): PhaseMarginData | null {
  const heures = phaseHoursSpent(phase, tsCons);
  const vendu = phaseRevenue(phase, heures);
  const cout = phaseCost(phase, tsByDev, costMap);
  if (vendu <= 0 && cout <= 0) return null;
  return marginData(vendu, cout, heures);
}
